import type React from "react";
import { theme, inputStyle, btnSecondary, type as text } from "../theme";
import type { SortOption } from "../lib/registerExport";

// The one toolbar every register page uses: search, sort and export, plus the summary tiles that
// sit above it. Pages keep their own filter chips beside it; this only adds what every register
// was missing (docs/NAVIGATION.md "Registers").

export function RegisterToolbar<T>({
  search, onSearch, placeholder = "Search", sortOptions, sort, onSort, onExport, exportLabel = "Export CSV", count, children,
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder?: string;
  sortOptions?: SortOption<T>[];
  sort?: string;
  onSort?: (key: string) => void;
  onExport?: () => void;
  exportLabel?: string;
  /** Rows shown after filtering, e.g. "12 of 40". */
  count?: { shown: number; total: number };
  children?: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "0 0 12px" }}>
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{ ...inputStyle, width: 240, maxWidth: "100%", padding: "7px 10px" }}
      />
      {sortOptions && sortOptions.length > 0 && onSort && (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: text.hint.fontSize, color: theme.color.subtle }}>
          Sort
          <select value={sort} onChange={(e) => onSort(e.target.value)} style={{ ...inputStyle, width: "auto", padding: "7px 10px" }}>
            {sortOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </label>
      )}
      {children}
      <span style={{ flex: 1 }} />
      {count && (
        <span style={{ ...text.hint }}>
          {count.shown === count.total ? `${count.total} shown` : `${count.shown} of ${count.total}`}
        </span>
      )}
      {onExport && (
        <button type="button" onClick={onExport} style={{ ...btnSecondary, padding: "7px 12px", fontSize: text.hint.fontSize }}>
          {exportLabel}
        </button>
      )}
    </div>
  );
}

export type TileTone = "neutral" | "accent" | "success" | "warning" | "danger";

const TONES: Record<TileTone, { bg: string; text: string; border: string }> = {
  neutral: { bg: theme.color.panel, text: theme.color.heading, border: "var(--c-line)" },
  accent: { bg: theme.color.accentBg, text: theme.color.accentText, border: theme.color.accent },
  success: { bg: theme.color.successBg, text: theme.color.successText, border: theme.color.success },
  warning: { bg: theme.color.warningBg, text: theme.color.warningText, border: theme.color.warning },
  danger: { bg: theme.color.dangerBg, text: theme.color.dangerText, border: theme.color.danger },
};

export interface SummaryTile {
  key: string;
  label: string;
  value: number | string;
  hint?: string;
  tone?: TileTone;
  /** Clicking the tile applies this filter; the active tile is outlined. */
  onClick?: () => void;
  active?: boolean;
}

/** Summary tiles above a register; each one filters the list below when clicked. */
export function SummaryTiles({ tiles }: { tiles: SummaryTile[] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, margin: "0 0 14px" }}>
      {tiles.map((t) => {
        const tone = TONES[t.tone ?? "neutral"];
        const Tag = t.onClick ? "button" : "div";
        return (
          <Tag
            key={t.key}
            type={t.onClick ? "button" : undefined}
            onClick={t.onClick}
            aria-pressed={t.onClick ? !!t.active : undefined}
            style={{
              textAlign: "left", fontFamily: theme.font, cursor: t.onClick ? "pointer" : "default",
              background: tone.bg, borderRadius: theme.radius.md, padding: "12px 14px",
              border: `${t.active ? 2 : 1}px solid ${t.active ? theme.color.accent : tone.border}`,
            }}
          >
            <div style={{ ...text.stat, color: tone.text }}>{t.value}</div>
            <div style={{ ...text.statLabel, marginTop: 4 }}>{t.label}</div>
            {t.hint && <div style={{ ...text.meta, color: theme.color.subtle, marginTop: 2 }}>{t.hint}</div>}
          </Tag>
        );
      })}
    </div>
  );
}
